import clsx from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';

import Button from '@/src/components/common/Elements/Button/Button';
import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import InlineImage from '@/src/components/common/Elements/Image/InlineImage';
import SectionLoader from '@/src/components/common/Elements/Spinner/SectionLoader/SectionLoader';
import {
  DEFAULT_CURRENCY,
  DEFAULT_EMPTY_VALUE,
} from '@/src/components/project/Inbox2/components/EntryPanel/FileEntryViewer/components/InfoPanel/Treatment/Config';
import ActionHeader from '@/src/components/project/Inbox2/components/EntryPanel/FileEntryViewer/components/InfoPanel/Treatment/Elements/ActionHeader/ActionHeader';
import Table from '@/src/components/project/Inbox2/components/EntryPanel/FileEntryViewer/components/InfoPanel/Treatment/Elements/Table/Table';
import { mapTreatments } from '@/src/components/project/Inbox2/components/EntryPanel/FileEntryViewer/components/InfoPanel/Treatment/Helper';
import { useTreatments } from '@/src/components/project/Inbox2/components/EntryPanel/FileEntryViewer/components/InfoPanel/Treatment/store/treatments.store';
import { Treatment } from '@/src/components/project/Inbox2/components/EntryPanel/FileEntryViewer/components/InfoPanel/Treatment/types/treatment';
import type { FileEntry } from '@/src/components/project/Inbox2/types';
import { useCompleteListPagination } from '@/src/hooks/useListCompletePagination';
import { parseNumberToStringWithSeparator } from '@/src/util/numbers';
import { cn } from '@/src/util/ui';

import { useGetLibraryItemTransactions } from '../../../api/getLibraryItemTransactions';

const TREATMENTS_LIMIT = 10;

type TreatmentListProps = {
  fileEntry: FileEntry;
};

const TreatmentList = ({ fileEntry }: TreatmentListProps) => {
  const documentId = fileEntry.file.id;
  const { setForm, setTreatment } = useTreatments();

  const [treatmentList, setTreatmentList] = useState<Array<Treatment>>([]);
  const treatmentsQuery = useGetLibraryItemTransactions({
    libraryItemId: documentId,
  });

  const isLoading = treatmentsQuery.isLoading;
  const isFetching = treatmentsQuery.isFetching;

  const {
    paginatedList,
    hasMore,
    loadMore,
    isLoading: isPaginating,
  } = useCompleteListPagination({
    list: treatmentList,
    maxPerPage: TREATMENTS_LIMIT,
  });

  const handleClickTreatment = useCallback(
    (item: Treatment) => {
      setTreatment(item as Treatment);
    },
    [setTreatment]
  );

  useEffect(() => {
    if (!treatmentsQuery.data) {
      return;
    }

    const treatments = mapTreatments(treatmentsQuery.data.data.transactions) as Treatment[];
    setTreatmentList(treatments);
  }, [treatmentsQuery.data]);

  return (
    <React.Fragment>
      <ActionHeader fileEntry={fileEntry} hasPublishedTreatment={treatmentList.length > 0} />

      <div
        className={cn(
          'treatmentList--container',
          'tw-overflow-y-auto tw-scroll-styles',
          'tw-h-[calc(100%-63px)]'
        )}
      >
        <Table
          data={paginatedList}
          onClickRow={handleClickTreatment}
          containerClassName={clsx('!tw-border-0 !tw-border-t-[1px]')}
          rowClassName={clsx(
            'tw-cursor-pointer hover:tw-bg-cfo-blue-200 active:tw-bg-cfo-blue-200'
          )}
          columns={[
            {
              title: '',
              width: '10%',
              Cell({ entry }) {
                if (!entry.draftId) {
                  return (
                    <InlineImage
                      src={`url(/images/quickbooks-icon1.png)`}
                      widthClasses={clsx('tw-w-[15px]')}
                      heightClasses={clsx('tw-min-h-[15px]')}
                    />
                  );
                }
                return entry.draftId && entry.automationId && !entry.modifiedBy ? (
                  <Icon
                    icon={ICONS.automation_on({
                      strokeClasses: 'tw-stroke-cfo-purple-200 tw-fill-cfo-purple-200',
                      width: '16px',
                      height: '16px',
                    })}
                    className={clsx('tw-text-[15px] tw-mr-[5px]')}
                  />
                ) : (
                  <Icon icon={ICONS.draft_icon({ className: '' })} />
                );
              },
            },
            {
              title: 'Type',
              width: '25%',
              Cell({ entry }) {
                const typeDisplayName = entry.typeDisplayName;
                return typeDisplayName ? (
                  <span>{typeDisplayName}</span>
                ) : (
                  <span className={clsx('tw-text-cfo-grey-600')}>[Type]</span>
                );
              },
            },
            {
              title: 'Document Ref',
              width: '35%',
              Cell({ entry }) {
                const referenceNumber = entry.payload.referenceNumber;
                if (entry.draftId) {
                  return referenceNumber ? (
                    <span>{referenceNumber}</span>
                  ) : (
                    <span className={clsx('tw-text-cfo-grey-600')}>[Document Ref]</span>
                  );
                }
                return <span> {referenceNumber || DEFAULT_EMPTY_VALUE}</span>;
              },
            },
            {
              title: 'Amount',
              width: '50%',
              textAlignment: 'tw-text-right',
              Cell({ entry }) {
                const totalAmount = entry.payload.totalAmount;
                const currency = entry.payload?.currency?.id || DEFAULT_CURRENCY.id;
                const amount = `${currency} ${parseNumberToStringWithSeparator(totalAmount, 2)}`;
                if (entry.draftId) {
                  return totalAmount > 0.0 ? (
                    <span>{amount}</span>
                  ) : (
                    <span className={clsx('tw-text-cfo-grey-600')}>[Amount]</span>
                  );
                }
                return <span>{amount}</span>;
              },
            },
          ]}
          isLoading={isLoading || isFetching}
        />

        {treatmentList.length < 1 && !isLoading && (
          <div
            className={clsx(
              'tw-flex tw-items-center tw-justify-center',
              'tw-text-center',
              'tw-min-h-[calc(100vh-340px)]'
            )}
          >
            <h2
              className={clsx(
                // (!) is not removed here as it is to override the styles from vendor.css
                'tw-font-brandonTextMedium !tw-text-[14px] tw-text-cfo-grey-600',
                'tw-m-0'
              )}
            >
              No available data
            </h2>
          </div>
        )}

        {isPaginating && <SectionLoader />}
        {hasMore && !isPaginating && (
          <div className={clsx('tw-w-full', 'tw-text-right', 'tw-py-[15px] tw-px-[20px] ')}>
            <Button
              className={clsx(
                'tw-font-brandonTextMedium tw-text-cfo-blue-600',
                'tw-mr-0',
                'hover:tw-underline'
              )}
              onClick={loadMore}
            >
              Load More
            </Button>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default TreatmentList;
